import React, { Component } from 'react';
import { withTheme } from 'styled-components';

import ValueWithLabel from '../common/ValueWithLabel';
import { Container, FooterActions, CourseRow, DueAndPlannedWrapper, HiddenRow, TitleWrapper } from './TaskCard.styles';
import { TaskType } from 'shovel-lib/types';
import { MiniText, SmallText } from '../../utils/typography';
import { Column, Row } from '../common/layoutUtils';
import { lightTheme } from '../../assets/themes';
import { DEFAULT_TASK_CATEGORY_EMOJI } from '../../utils/constants/task';
import t from '../../i18n/t';
import PlannedTag from './partials/PlannedTag';
import PileEstimateControl from './partials/PileEstimateControl';
import { minutesToDurationInHours } from '../../utils/filters';
import colors from '../../utils/colors';
import { EmojiPreview } from '@components/common/emoji/EmojiPreview';
import TaskPriority from '@components/pile/components/TaskPriority';
import { withAlpha } from '@utils/colorUtils';
import { isTaskFullyUsed } from '@utils/taskPlanning';

type Props = {
  backgroundColor?: string;
  due?: string;
  start?: string;
  colorHex: string;
  type: TaskType;
  cushionComponent: React.ReactNode;
  progressComponent?: React.ReactNode;
  cushion?: number;
  course: string;
  title: string;
  priority?: number;
  subtitle?: string;
  styles?: object;
  iconSize?: number;
  timePlanned?: number;
  onClick?: (clickData: any) => void;
  className?: string;
  categoryName?: string;
  categoryEmoji?: string;
  // The task's category and course, for a first estimate's "Also for 12 other Homework".
  categoryId?: number;
  courseId?: number;
  readingSourceName?: string;
  taskId?: number;
  /** Opt in to the inline estimate control. The charts show cards no one is meant to act on. */
  canEstimate?: boolean;
  onEstimateOpenChange?: (open: boolean) => void;
  onEstimateSaved?: () => void;
  subtitleWidth?: string;
  theme?: any;
  parentTheme?: any;
  isPile?: any;
  delete?: any;
  isCompleted?: any;
  dragDisabled?: boolean;
  renderMarkBtn?: any;
  renderDeleteBtn?: any;
  duration?: number;
  timeNeed?: number;
  timeSpent?: number;
  hasNoActions?: boolean;
  noPlannedTooltip?: boolean;
  hasSidebar?: boolean;
  changePriority?: (priority) => void;
};

const CUSHION_SLOT_STYLE = {
  flexShrink: 0,
  height: 16,
  margin: '-4px -6px 0 0',
  alignSelf: 'flex-start',
  alignItems: 'flex-start',
  lineHeight: 0
};

class TaskCard extends Component<Props, { addTimeOpen: boolean }> {
  state = { addTimeOpen: false };

  /**
   * Every minute it needed was planned and used. Either it is finished or it needs more time, so its footer's Mark
   * complete is green, beside +time (Petr, 2026-10-09).
   */
  isFullyUsed = () => {
    const { timeNeed, timePlanned, timeSpent, isCompleted, isPile } = this.props;
    return !!isPile && !isCompleted && isTaskFullyUsed({ timeNeed, timePlanned, timeSpent });
  };

  setAddTimeOpen = (addTimeOpen: boolean) => {
    this.setState({ addTimeOpen });
    this.props.onEstimateOpenChange?.(addTimeOpen);
  };

  // The card's time tag for this much time needed. The estimate control shows it as soon as an estimate is picked, so
  // the card looks the same before the save is through as after.
  plannedTag = (timeNeed: number) => {
    const { timePlanned = 0, timeSpent, theme, parentTheme, noPlannedTooltip } = this.props;
    return (
      <PlannedTag
        {...{ timeNeed, timePlanned, timeSpent }}
        isTaskCard
        bare
        font={10}
        parentTheme={theme || parentTheme}
        noTooltip={noPlannedTooltip}
      />
    );
  };

  renderPlannedTag = () => {
    const {
      isCompleted,
      timeNeed,
      timePlanned,
      isPile,
      timeSpent,
      taskId,
      title,
      colorHex,
      onClick,
      readingSourceName,
      canEstimate,
      onEstimateOpenChange,
      onEstimateSaved,
      categoryId,
      categoryName,
      courseId
    } = this.props;
    if (isCompleted && isPile && !isNaN(timeSpent!)) {
      return <ValueWithLabel value={minutesToDurationInHours(timeSpent!)} label={t.TIME_SPENT} font={10} />;
    }
    if (isCompleted || timeNeed === undefined || timePlanned === undefined) return null;

    // With neither an estimate nor anything planned there is no figure to show, and Auto-plan skips the task
    // entirely. Wherever the card is one you act on, that bare icon becomes the way to fix it. Time already
    // spent means the estimate was there and got used up, which the tag says instead.
    if (canEstimate && taskId != null && !timeNeed && !timePlanned && !timeSpent) {
      return (
        <PileEstimateControl
          taskId={taskId}
          title={title}
          color={colorHex}
          onOpenTask={onClick}
          isReading={!!readingSourceName}
          onOpenChange={onEstimateOpenChange}
          onSaved={onEstimateSaved}
          renderEstimate={this.plannedTag}
          category={
            categoryId != null && courseId != null && categoryName
              ? { id: categoryId, courseId, name: categoryName }
              : undefined
          }
        />
      );
    }

    return this.plannedTag(timeNeed);
  };

  /**
   * How much of the task is done: the course colour across that share of the card, and a line where it ends (Petr,
   * 2026-10-08: the faint tint alone was hard to see). Drawn as the card's own background, so it sits under the text;
   * an element over the card tinted the text, and one under it would have needed the card to stack its own layers,
   * which hid the actions row it shows below itself on hover under the next card.
   * timeSpent : timeNeed = x : (100 - x), so x = timeSpent * 100 / (timeNeed + timeSpent)
   */
  progressBackground = (theme: any): React.CSSProperties | undefined => {
    const { colorHex, timeSpent, timeNeed = 0, isCompleted } = this.props;
    if (!timeSpent || isCompleted || !colorHex || !/^#[0-9a-f]{6}$/i.test(colorHex)) return undefined;
    // Nothing still needed means all of it is done: the whole card, not none of it.
    const percents = timeNeed > 0 ? Math.min(Math.floor((timeSpent * 100) / (timeNeed + timeSpent)), 100) : 100;
    if (percents <= 0) return undefined;
    const fill = withAlpha(colorHex, theme.taskProgressOpacity);
    const edge = percents < 100 ? withAlpha(colorHex, theme.taskProgressEdgeOpacity) : fill;
    return {
      backgroundImage: `linear-gradient(to right, ${fill} calc(${percents}% - 2px), ${edge} calc(${percents}% - 2px), ${edge} ${percents}%, transparent ${percents}%)`
    };
  };

  // Add time, as the same thin pill as Estimate, always in the footer: beside Used it made the card too busy. Not
  // before the task has an estimate, which the card's Estimate asks for instead (Petr, 2026-10-09). A reading's adds
  // pages (2026-10-22).
  renderAddTime = () => {
    const { canEstimate, taskId, title, colorHex, onClick, onEstimateSaved, isCompleted, readingSourceName } = this.props;
    const { timeNeed, timePlanned, timeSpent } = this.props;
    if (!canEstimate || taskId == null || isCompleted || (!timeNeed && !timePlanned && !timeSpent)) return null;
    return (
      <PileEstimateControl
        taskId={taskId}
        title={title}
        color={colorHex}
        onOpenTask={onClick}
        isReading={!!readingSourceName}
        addTime
        onOpenChange={this.setAddTimeOpen}
        onSaved={onEstimateSaved}
      />
    );
  };

  renderHiddenRow = () => {
    const { isPile, colorHex, renderMarkBtn, renderDeleteBtn, backgroundColor, hasSidebar } = this.props;
    if (!isPile) return null;
    const fullyUsed = this.isFullyUsed();

    return (
      // Trash on the left, Mark complete on the right (Petr): the row spreads them to its two ends, with Add time just
      // before Mark complete.
      <HiddenRow
        borderColor={colorHex}
        backgroundColor={backgroundColor}
        hasSidebar={hasSidebar}
        $shown={this.state.addTimeOpen}
      >
        {renderDeleteBtn()}
        <FooterActions>
          {this.renderAddTime()}
          {renderMarkBtn(fullyUsed)}
        </FooterActions>
      </HiddenRow>
    );
  };

  render() {
    const {
      due,
      start,
      cushionComponent,
      colorHex,
      course,
      title,
      styles,
      hasSidebar,
      onClick,
      className,
      isCompleted,
      categoryName,
      categoryEmoji,
      readingSourceName,
      hasNoActions,
      backgroundColor,
      dragDisabled,
      priority = 0,
      changePriority
    } = this.props;
    const theme = this.props.theme || this.props.parentTheme || lightTheme;

    return (
      // cardTheme added because of cushion tooltip
      <Container
        hasSidebar={hasSidebar}
        style={{ ...this.progressBackground(theme), ...styles }}
        onClick={e => {
          if (!onClick || e.defaultPrevented) return;
          onClick(e);
        }}
        className={className}
        colorHex={colorHex || colors.randomTaskColor}
        isDraggable={!dragDisabled && !isCompleted}
        hasNoActions={hasNoActions}
        backgroundColor={backgroundColor}
        cardTheme={theme}
      >
        <Row>
          <CourseRow color={theme.textColor}>
            <SmallText color={colorHex}>{course || t.RANDOM}</SmallText>
            {categoryName && (
              <MiniText className={'task-category'}>
                <EmojiPreview emoji={categoryEmoji || DEFAULT_TASK_CATEGORY_EMOJI} />
                {categoryName}
              </MiniText>
            )}
            {readingSourceName && <MiniText>{readingSourceName}</MiniText>}
          </CourseRow>
          {/* height: 16 - Make card height independent from cushionComponent height. Nudged into the card's top-right
              corner, about 3px from each edge (Petr, 2026-10-15). */}
          <Row style={CUSHION_SLOT_STYLE}>{cushionComponent}</Row>
        </Row>
        <Row>
          <TitleWrapper color={theme.textStrongColor}>{title}</TitleWrapper>
          {priority >= 0 && (
            <TaskPriority
              priority={priority}
              onChange={
                !changePriority
                  ? undefined
                  : (value, e) => {
                      e.preventDefault();
                      changePriority(value);
                    }
              }
              fontSize={13}
              right
              theme={theme}
            />
          )}
        </Row>
        <DueAndPlannedWrapper>
          <Column>
            {start && (
              //valueStyle and labelStyle are here because of cushion tooltip card (it renders outside theme provider)
              <ValueWithLabel
                font={10}
                label={t.START}
                value={start}
                valueStyle={{ color: theme.textStrongColor }}
                labelStyle={{ color: theme.textColor }}
              />
            )}
            {due && (
              <ValueWithLabel
                font={10}
                label={t.DUE}
                value={due}
                valueStyle={{ color: theme.textStrongColor }}
                labelStyle={{ color: theme.textColor }}
              />
            )}
          </Column>
          {this.renderPlannedTag()}
        </DueAndPlannedWrapper>
        {this.renderHiddenRow()}
      </Container>
    );
  }
}

export default withTheme(TaskCard);
